import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail,
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProgress, setUserProgress] = useState(null);

  async function signup(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      try {
        await updateProfile(result.user, {
          displayName: displayName
        });
      } catch (profileError) {
        console.warn('Failed to update profile, but user was created:', profileError);
        // Continue even if profile update fails
      }

      // Create user document in Firestore
      try {
        const userData = {
          displayName: displayName,
          email: email,
          createdAt: new Date(),
          progress: {
            week1: { completed: 0, total: 8 },
            week2: { completed: 0, total: 7 },
            week3: { completed: 0, total: 6 },
            week4: { completed: 0, total: 5 }
          },
          completedActivities: {
            week1: [],
            week2: [],
            week3: [],
            week4: []
          }
        };
        
        console.log('Creating user document in Firestore for user:', result.user.uid);
        await setDoc(doc(db, 'users', result.user.uid), userData);
        console.log('Successfully created user document in Firestore');
      } catch (firestoreError) {
        console.error('Failed to create user document in Firestore:', firestoreError);
        console.error('Firestore error details:', {
          code: firestoreError.code,
          message: firestoreError.message
        });
        // User is still created, but we couldn't save their progress data
        // This shouldn't prevent signup from succeeding
      }

      return result;
    } catch (error) {
      console.error('Signup error:', error);
      throw error; // Re-throw the error so the UI can handle it
    }
  }

  async function login(email, password) {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error; // Re-throw the error so the UI can handle it
    }
  }

  async function logout() {
    return await signOut(auth);
  }

  async function requestPasswordReset(email) {
    try {
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  async function saveUserProgress(progress, completedActivities = null) {
    if (currentUser) {
      try {
        const updateData = { progress };
        if (completedActivities) {
          updateData.completedActivities = completedActivities;
        }
        await setDoc(doc(db, 'users', currentUser.uid), updateData, { merge: true });
        setUserProgress(progress);
      } catch (error) {
        console.error('Error saving user progress:', error);
      }
    }
  }

  async function loadUserProgress() {
    if (currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProgress(userData.progress);
          return {
            progress: userData.progress,
            completedActivities: userData.completedActivities || {
              week1: [],
              week2: [],
              week3: [],
              week4: []
            },
            reflectionAnswers: userData.reflectionAnswers || {
              week1: {},
              week2: {},
              week3: {},
              week4: {}
            },
            journeyPersonalization: userData.journeyPersonalization || null
          };
        } else {
          // Create default user document if it doesn't exist
          const defaultData = {
            progress: {
              week1: { completed: 0, total: 8 },
              week2: { completed: 0, total: 7 },
              week3: { completed: 0, total: 6 },
              week4: { completed: 0, total: 5 }
            },
            completedActivities: {
              week1: [],
              week2: [],
              week3: [],
              week4: []
            },
            journeyPersonalization: null
          };
          await setDoc(doc(db, 'users', currentUser.uid), defaultData);
          setUserProgress(defaultData.progress);
          return defaultData;
        }
      } catch (error) {
        console.error('Error loading user progress:', error);
        return null;
      }
    }
    return null;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Load user progress when user signs in
        await loadUserProgress();
      } else {
        setUserProgress(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProgress,
    signup,
    login,
    requestPasswordReset,
    logout,
    saveUserProgress,
    loadUserProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}